import React, { useState, useRef, useMemo, useEffect } from 'react';
import axios from "axios";
import {
  Boxes,
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  ChevronsUpDown,
  ChevronUp,
  Download,
  IndianRupee,
  PackagePlus,
  Pencil,
  Rows3,
  Search,
  ShoppingCart,
  Tags,
  X,
} from 'lucide-react';
import { API_BASE_URL } from '../Config.js';
import styles from './View.module.css';

/* ------------------------------------------------------------------
   Helpers for stock value / grouping
   ------------------------------------------------------------------ */

// Values of group_id that mean "this row is a standalone item"
const SINGLE_ITEM_GROUP_VALUES = ['', 'NA', 'N/A', 'NULL', 'NONE', '-'];

const isSingleItem = (groupId) => {
  if (groupId === null || groupId === undefined) return true;
  return SINGLE_ITEM_GROUP_VALUES.includes(String(groupId).trim().toUpperCase());
};

const normalizeGroupId = (groupId) => String(groupId).trim().toUpperCase();

// price / wholeSalePrice arrive as Strings from the backend
const toNumber = (value) => {
  if (value === null || value === undefined || value === '') return 0;
  const parsed = parseFloat(String(value).replace(/[^0-9.-]/g, ''));
  return Number.isNaN(parsed) ? 0 : parsed;
};

const getStockValue = (item) => toNumber(item.wholeSalePrice) * toNumber(item.quantity);

const formatAmount = (value) =>
  new Intl.NumberFormat('en-IN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value || 0);

const formatCount = (value) =>
  new Intl.NumberFormat('en-IN', { maximumFractionDigits: 2 }).format(value || 0);

/* ------------------------------------------------------------------
   Table configuration
   ------------------------------------------------------------------ */

// Available quantity at or below this is flagged as low stock
const LOW_STOCK_LIMIT = 5;

const STOCK_LEVELS = [
  { value: 'all', label: 'All stock levels' },
  { value: 'in', label: 'In stock' },
  { value: 'low', label: `Low stock (1–${LOW_STOCK_LIMIT})` },
  { value: 'out', label: 'Out of stock' },
];

const getStockLevel = (quantity) => {
  const qty = toNumber(quantity);
  if (qty <= 0) return 'out';
  if (qty <= LOW_STOCK_LIMIT) return 'low';
  return 'in';
};

const matchesStockLevel = (item, level) => {
  if (level === 'all') return true;
  const qty = toNumber(item.quantity);
  if (level === 'in') return qty > 0;
  if (level === 'low') return qty > 0 && qty <= LOW_STOCK_LIMIT;
  return qty <= 0;
};

const LEVEL_CLASS = { in: styles.qtyIn, low: styles.qtyLow, out: styles.qtyOut };

const SEARCH_FIELDS = ['itemCode', 'itemName', 'description', 'itemType', 'itemColor', 'itemSize', 'itemCategory', 'group_id'];

// The index of each field is its arrow-key column
const EDIT_FIELDS = ['quantity', 'price', 'wholeSalePrice'];

// 0 shows every row on one page
const PAGE_SIZE_OPTIONS = [25, 50, 100, 0];

const SORT_VALUES = {
  sno: (item) => toNumber(item.sno),
  itemCode: (item) => item.itemCode,
  description: (item) => item.description,
  itemType: (item) => item.itemType,
  itemColor: (item) => item.itemColor,
  itemSize: (item) => item.itemSize,
  itemCategory: (item) => item.itemCategory,
  price: (item) => toNumber(item.price),
  wholeSalePrice: (item) => toNumber(item.wholeSalePrice),
  quantity: (item) => toNumber(item.quantity),
  stockValue: getStockValue,
  group_id: (item) => item.group_id,
};

const compareText = (a, b) =>
  String(a ?? '').localeCompare(String(b ?? ''), undefined, { numeric: true, sensitivity: 'base' });

const uniqueValues = (items, field) =>
  [...new Set(
    items
      .map((item) => item[field])
      .filter((value) => value !== null && value !== undefined && String(value).trim() !== '')
      .map(String)
  )].sort(compareText);

// Up to five page numbers around the current page
const getPageNumbers = (current, total) => {
  const start = Math.max(1, Math.min(current - 2, total - 4));
  const end = Math.min(total, start + 4);
  return Array.from({ length: end - start + 1 }, (_, i) => start + i);
};

const cx = (...classNames) => classNames.filter(Boolean).join(' ');

const SortHeader = ({ label, sortKey, sort, onSort, alignRight }) => {
  const active = sort?.key === sortKey;
  const Icon = !active ? ChevronsUpDown : sort.dir === 'asc' ? ChevronUp : ChevronDown;

  return (
    <th
      className={alignRight ? styles.alignRight : undefined}
      aria-sort={active ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}
    >
      <button
        type="button"
        className={cx(styles.sortBtn, active && styles.sortBtnActive)}
        onClick={() => onSort(sortKey)}
      >
        {label}
        <Icon size={14} aria-hidden="true" />
      </button>
    </th>
  );
};

const SummaryCard = ({ icon: Icon, label, value, hint, hintTitle, primary }) => (
  <div className={cx(styles.summaryCard, primary && styles.summaryCardPrimary)}>
    <span className={styles.summaryIcon} aria-hidden="true">
      <Icon size={18} />
    </span>
    <div className={styles.summaryText}>
      <span className={styles.summaryLabel}>{label}</span>
      <span className={styles.summaryValue}>{value}</span>
      {hint && <span className={styles.summaryHint} title={hintTitle || hint}>{hint}</span>}
    </div>
  </div>
);

const View = ({ data, onUpdateSuccess, loading = false }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [maxQuantity, setMaxQuantity] = useState('');
  const [colorFilter, setColorFilter] = useState('');
  const [sizeFilter, setSizeFilter] = useState('');
  const [stockLevel, setStockLevel] = useState('all');
  const [sort, setSort] = useState(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [editableData, setEditableData] = useState({});
  const [isEditingQuantity, setIsEditingQuantity] = useState(false);
  const [isEditingPrice, setIsEditingPrice] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [orderingId, setOrderingId] = useState(null);

  const inputRefs = useRef({});
  const tableWrapRef = useRef(null);

  const handlePlaceOrder = async (barcodedId) => {
    setOrderingId(barcodedId);
    try {
      const user = JSON.parse(sessionStorage.getItem('user'));
      const storeId = user?.storeId;

      const response = await fetch(`${API_BASE_URL}/user/create_order`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams({
          barcodedId: barcodedId,
          storeId: storeId
        }),
      });

      if (response.ok) {
        const status = await response.text();
        alert(`Order placed successfully: ${status}`);
      } else {
        alert('Failed to place order.');
      }
    } catch (error) {
      console.error('Error placing order:', error);
      alert('An error occurred while placing the order.');
    } finally {
      setOrderingId(null);
    }
  };

  const handleInputChange = (e, id, field) => {
    const { value } = e.target;
    setEditableData((prev) => ({
      ...prev,
      [id]: {
        ...prev[id],
        [field]: value,
      },
    }));
  };

  const handleUpdate = async () => {
    setIsSubmitting(true);
    try {
      const user = JSON.parse(sessionStorage.getItem('user'));
      if (!user) {
        alert('User not found in local storage.');
        return;
      }

      const itemAddModel = Object.keys(editableData).map((id) => ({
        barcodedId: id,
        price: editableData[id]?.price || data.find((item) => item.itemBarcodeID === id)?.price,
        wholeSalePrice: editableData[id]?.wholeSalePrice || data.find((item) => item.itemBarcodeID === id)?.wholeSalePrice,
        quantity: editableData[id]?.quantity || data.find((item) => item.itemBarcodeID === id)?.quantity,
      }));

      const inventoryUpdateModel = {
        itemAddModel: itemAddModel,
        user: user
      };

      const response = await fetch(`${API_BASE_URL}/inventory/update_inventory`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(inventoryUpdateModel),
      });

      if (response.ok) {
        alert('Updates submitted successfully');
        setEditableData({});
        setIsEditingQuantity(false);
        setIsEditingPrice(false);
        onUpdateSuccess();
      } else {
        alert('Failed to update inventory.');
      }
    } catch (error) {
      console.error('Error updating inventory:', error);
      alert('An error occurred while updating the inventory.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDiscard = () => {
    setEditableData({});
    setIsEditingQuantity(false);
    setIsEditingPrice(false);
  };

  const handleSort = (key) => {
    setSort((current) => {
      if (!current || current.key !== key) return { key, dir: 'asc' };
      if (current.dir === 'asc') return { key, dir: 'desc' };
      return null;
    });
  };

  const clearFilters = () => {
    setSearchTerm('');
    setColorFilter('');
    setSizeFilter('');
    setStockLevel('all');
    setMaxQuantity('');
  };

  /* ------------------------------------------------------------------
     Filtering + sorting
     ------------------------------------------------------------------ */
  const colorOptions = useMemo(() => uniqueValues(data || [], 'itemColor'), [data]);
  const sizeOptions = useMemo(() => uniqueValues(data || [], 'itemSize'), [data]);

  const filteredData = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();

    return (data || []).filter((item) => {
      if (term && !SEARCH_FIELDS.some((field) => item[field] && String(item[field]).toLowerCase().includes(term))) {
        return false;
      }
      if (colorFilter && String(item.itemColor) !== colorFilter) return false;
      if (sizeFilter && String(item.itemSize) !== sizeFilter) return false;
      if (!matchesStockLevel(item, stockLevel)) return false;

      // Only apply quantity filter when maxQuantity is provided
      if (maxQuantity !== '') {
        return item.quantity !== undefined && toNumber(item.quantity) <= Number(maxQuantity);
      }
      return true;
    });
  }, [data, searchTerm, colorFilter, sizeFilter, stockLevel, maxQuantity]);

  const sortedData = useMemo(() => {
    if (sort) {
      const getValue = SORT_VALUES[sort.key];
      const direction = sort.dir === 'asc' ? 1 : -1;
      return [...filteredData].sort((a, b) => {
        const left = getValue(a);
        const right = getValue(b);
        const result = typeof left === 'number' && typeof right === 'number'
          ? left - right
          : compareText(left, right);
        return result * direction;
      });
    }

    // Sort by quantity descending when the quantity filter is applied and no column is sorted
    if (maxQuantity !== '') {
      return [...filteredData].sort((a, b) => toNumber(b.quantity) - toNumber(a.quantity));
    }

    return filteredData;
  }, [filteredData, sort, maxQuantity]);

  /* ------------------------------------------------------------------
     Stock value summary.

     Rows sharing the same group_id are the same physical stock shown
     under different display names (CS FULL PANT 20 / VMS FULL PANT 20
     both = NX/VMS/FP/20), so only the first row of each group is added
     to the totals. group_id of NA / "" / null is always a standalone item
     and is always counted.
     ------------------------------------------------------------------ */
  const { countedIds, summary } = useMemo(() => {
    const seenGroups = new Set();
    const counted = new Set();

    let totalValue = 0;
    let totalQuantity = 0;
    let uniqueItems = 0;
    let duplicateRows = 0;
    let duplicateValue = 0;

    filteredData.forEach((item) => {
      const value = getStockValue(item);

      if (!isSingleItem(item.group_id)) {
        const key = normalizeGroupId(item.group_id);
        if (seenGroups.has(key)) {
          duplicateRows += 1;
          duplicateValue += value;
          return; // already counted under this group
        }
        seenGroups.add(key);
      }

      counted.add(item.itemBarcodeID);
      uniqueItems += 1;
      totalValue += value;
      totalQuantity += toNumber(item.quantity);
    });

    return {
      countedIds: counted,
      summary: {
        totalRows: filteredData.length,
        uniqueItems,
        duplicateRows,
        duplicateValue,
        totalQuantity,
        totalValue,
      },
    };
  }, [filteredData]);

  /* ------------------------------------------------------------------
     Pagination
     ------------------------------------------------------------------ */
  const totalRows = sortedData.length;
  const totalPages = pageSize ? Math.max(1, Math.ceil(totalRows / pageSize)) : 1;
  const currentPage = Math.min(page, totalPages);
  const pageStart = pageSize ? (currentPage - 1) * pageSize : 0;
  const pageEnd = pageSize ? Math.min(pageStart + pageSize, totalRows) : totalRows;
  const pageRows = sortedData.slice(pageStart, pageEnd);

  useEffect(() => {
    setPage(1);
  }, [searchTerm, colorFilter, sizeFilter, stockLevel, maxQuantity, sort, pageSize]);

  useEffect(() => {
    if (tableWrapRef.current) tableWrapRef.current.scrollTop = 0;
  }, [currentPage, pageSize]);

  const handleExport = async () => {
    setIsExporting(true);
    try {
      const response = await axios.post(`${API_BASE_URL}/inventory/export`, sortedData, {
        responseType: 'blob',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'inventory.xlsx');
      document.body.appendChild(link);
      link.click();

      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting data:', error);
      alert('Could not export stock. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };

  const handleKeyDown = (e, rowIndex, columnIndex) => {
    if (!['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key)) return;
    e.preventDefault();

    let newRow = rowIndex;
    let newCol = columnIndex;

    if (e.key === 'ArrowUp') {
      newRow = Math.max(rowIndex - 1, pageStart);
    } else if (e.key === 'ArrowDown') {
      newRow = Math.min(rowIndex + 1, pageEnd - 1);
    } else {
      // Step over columns that are not currently editable
      const step = e.key === 'ArrowRight' ? 1 : -1;
      for (let offset = 1; offset < EDIT_FIELDS.length; offset += 1) {
        const candidate = (columnIndex + step * offset + EDIT_FIELDS.length) % EDIT_FIELDS.length;
        if (inputRefs.current[`${rowIndex}-${candidate}`]) {
          newCol = candidate;
          break;
        }
      }
    }

    const nextRef = inputRefs.current[`${newRow}-${newCol}`];
    if (nextRef) {
      nextRef.focus();
    }
  };

  const hasActiveFilters = Boolean(searchTerm || colorFilter || sizeFilter || maxQuantity !== '' || stockLevel !== 'all');
  const isEditing = isEditingQuantity || isEditingPrice;
  const editedCount = Object.keys(editableData).length;

  const duplicateNote = summary.duplicateRows > 0
    ? `${formatCount(summary.duplicateRows)} grouped ${summary.duplicateRows === 1 ? 'row' : 'rows'} worth ₹ ${formatAmount(summary.duplicateValue)} left out of the total — same stock listed under another name.`
    : '';

  return (
    <div className={styles.root}>
      {/* Stock value summary */}
      <section className={styles.summaryGrid} aria-label="Stock summary">
        <SummaryCard
          primary
          icon={IndianRupee}
          label="Total stock value"
          value={`₹ ${formatAmount(summary.totalValue)}`}
          hint={summary.duplicateRows > 0
            ? `Excludes ₹ ${formatAmount(summary.duplicateValue)} in ${formatCount(summary.duplicateRows)} grouped rows`
            : 'Wholesale price × available quantity'}
          hintTitle={duplicateNote}
        />
        <SummaryCard icon={Boxes} label="Total quantity" value={formatCount(summary.totalQuantity)} />
        <SummaryCard icon={Tags} label="Unique items" value={formatCount(summary.uniqueItems)} />
        <SummaryCard
          icon={Rows3}
          label="Rows shown"
          value={formatCount(summary.totalRows)}
          hint={summary.totalRows !== data.length ? `of ${formatCount(data.length)} loaded` : undefined}
        />
      </section>

      <section className={styles.card}>
        {loading && (
          <div className={styles.loadingOverlay} aria-live="polite">
            <span className={styles.spinner} aria-label="Loading stock" />
          </div>
        )}

        <div className={styles.toolbar}>
          <div className={styles.filters}>
            <div className={styles.searchWrap}>
              <Search size={16} className={styles.searchIcon} aria-hidden="true" />
              <input
                type="search"
                placeholder="Search code, description, color, size, group…"
                aria-label="Search stock"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className={styles.searchInput}
              />
            </div>

            <select
              aria-label="Filter by color"
              className={cx(styles.control, colorFilter && styles.controlActive)}
              value={colorFilter}
              onChange={(e) => setColorFilter(e.target.value)}
            >
              <option value="">All colors</option>
              {colorOptions.map((color) => (
                <option key={color} value={color}>{color}</option>
              ))}
            </select>

            <select
              aria-label="Filter by size"
              className={cx(styles.control, sizeFilter && styles.controlActive)}
              value={sizeFilter}
              onChange={(e) => setSizeFilter(e.target.value)}
            >
              <option value="">All sizes</option>
              {sizeOptions.map((size) => (
                <option key={size} value={size}>{size}</option>
              ))}
            </select>

            <select
              aria-label="Filter by stock level"
              className={cx(styles.control, stockLevel !== 'all' && styles.controlActive)}
              value={stockLevel}
              onChange={(e) => setStockLevel(e.target.value)}
            >
              {STOCK_LEVELS.map((level) => (
                <option key={level.value} value={level.value}>{level.label}</option>
              ))}
            </select>

            <input
              type="number"
              min="0"
              placeholder="Max qty"
              aria-label="Maximum available quantity"
              value={maxQuantity}
              onChange={(e) => setMaxQuantity(e.target.value)}
              className={cx(styles.control, styles.qtyInput, maxQuantity !== '' && styles.controlActive)}
            />

            {hasActiveFilters && (
              <button type="button" className={styles.btnLink} onClick={clearFilters}>
                <X size={14} aria-hidden="true" />
                Clear
              </button>
            )}
          </div>

          <div className={styles.toolbarActions}>
            <button
              type="button"
              aria-pressed={isEditingPrice}
              className={cx(styles.btn, styles.btnToggle, isEditingPrice && styles.btnToggleOn)}
              onClick={() => setIsEditingPrice((value) => !value)}
            >
              <Pencil size={15} aria-hidden="true" />
              Edit price
            </button>
            <button
              type="button"
              aria-pressed={isEditingQuantity}
              className={cx(styles.btn, styles.btnToggle, isEditingQuantity && styles.btnToggleOn)}
              onClick={() => setIsEditingQuantity((value) => !value)}
            >
              <PackagePlus size={15} aria-hidden="true" />
              Add quantity
            </button>
            <button
              type="button"
              className={cx(styles.btn, styles.btnSecondary)}
              onClick={handleExport}
              disabled={isExporting || totalRows === 0}
            >
              <Download size={15} aria-hidden="true" />
              {isExporting ? 'Exporting…' : 'Export'}
            </button>
          </div>
        </div>

        <div className={styles.tableWrap} ref={tableWrapRef}>
          {totalRows > 0 ? (
            <table className={styles.table}>
              <thead>
                <tr>
                  <SortHeader label="S.No" sortKey="sno" sort={sort} onSort={handleSort} />
                  <SortHeader label="Item code" sortKey="itemCode" sort={sort} onSort={handleSort} />
                  <SortHeader label="Description" sortKey="description" sort={sort} onSort={handleSort} />
                  <SortHeader label="Type" sortKey="itemType" sort={sort} onSort={handleSort} />
                  <SortHeader label="Color" sortKey="itemColor" sort={sort} onSort={handleSort} />
                  <SortHeader label="Size" sortKey="itemSize" sort={sort} onSort={handleSort} />
                  <SortHeader label="School" sortKey="itemCategory" sort={sort} onSort={handleSort} />
                  <SortHeader label="Price (₹)" sortKey="price" sort={sort} onSort={handleSort} alignRight />
                  <SortHeader label="Wholesale (₹)" sortKey="wholeSalePrice" sort={sort} onSort={handleSort} alignRight />
                  <SortHeader label="Available" sortKey="quantity" sort={sort} onSort={handleSort} alignRight />
                  <SortHeader label="Stock value (₹)" sortKey="stockValue" sort={sort} onSort={handleSort} alignRight />
                  {isEditingQuantity && <th className={styles.alignCenter}>Add qty</th>}
                  <SortHeader label="Group" sortKey="group_id" sort={sort} onSort={handleSort} />
                  <th className={styles.alignCenter}>Action</th>
                </tr>
              </thead>
              <tbody>
                {pageRows.map((item, pageIndex) => {
                  const rowIndex = pageStart + pageIndex;
                  const stockValue = getStockValue(item);
                  const isCounted = countedIds.has(item.itemBarcodeID);
                  const grouped = !isSingleItem(item.group_id);
                  const level = getStockLevel(item.quantity);
                  const isOrdering = orderingId === item.itemBarcodeID;

                  return (
                    <tr
                      key={item.itemBarcodeID || rowIndex}
                      className={cx(
                        !isCounted && styles.duplicateRow,
                        editableData[item.itemBarcodeID] && styles.editedRow
                      )}
                    >
                      <td className={styles.muted}>{item.sno}</td>
                      <td className={styles.code}>{item.itemCode}</td>
                      <td className={styles.desc}>
                        <span className={styles.truncate} title={item.description}>{item.description}</span>
                      </td>
                      <td>{item.itemType}</td>
                      <td>{item.itemColor}</td>
                      <td>{item.itemSize}</td>
                      <td>
                        <span className={cx(styles.truncate, styles.truncateSchool)} title={item.itemCategory}>
                          {item.itemCategory}
                        </span>
                      </td>
                      <td className={styles.num}>
                        {isEditingPrice ? (
                          <input
                            type="number"
                            aria-label={`Price for ${item.itemCode}`}
                            value={editableData[item.itemBarcodeID]?.price ?? item.price}
                            onChange={(e) => handleInputChange(e, item.itemBarcodeID, 'price')}
                            ref={(el) => (inputRefs.current[`${rowIndex}-1`] = el)}
                            onKeyDown={(e) => handleKeyDown(e, rowIndex, 1)}
                            className={styles.editInput}
                          />
                        ) : (
                          item.price
                        )}
                      </td>
                      <td className={styles.num}>
                        {isEditingPrice ? (
                          <input
                            type="number"
                            aria-label={`Wholesale price for ${item.itemCode}`}
                            value={editableData[item.itemBarcodeID]?.wholeSalePrice ?? item.wholeSalePrice}
                            onChange={(e) => handleInputChange(e, item.itemBarcodeID, 'wholeSalePrice')}
                            ref={(el) => (inputRefs.current[`${rowIndex}-2`] = el)}
                            onKeyDown={(e) => handleKeyDown(e, rowIndex, 2)}
                            className={styles.editInput}
                          />
                        ) : (
                          item.wholeSalePrice
                        )}
                      </td>
                      <td className={styles.num}>
                        <span className={cx(styles.qtyPill, LEVEL_CLASS[level])}>
                          {item.quantity ?? 0}
                        </span>
                      </td>

                      <td className={styles.num}>
                        <span className={styles.valueCell}>
                          {grouped && (
                            <span
                              className={cx(styles.groupTag, isCounted && styles.groupTagCounted)}
                              title={
                                isCounted
                                  ? `Counted once for group ${item.group_id}`
                                  : `Not added to the total — already counted under group ${item.group_id}`
                              }
                            >
                              {isCounted ? 'group' : 'duplicate'}
                            </span>
                          )}
                          <span className={isCounted ? styles.valueAmount : styles.valueMuted}>
                            {formatAmount(stockValue)}
                          </span>
                        </span>
                      </td>

                      {isEditingQuantity && (
                        <td className={styles.alignCenter}>
                          <div className={styles.stepper}>
                            <button
                              type="button"
                              className={styles.stepperBtn}
                              aria-label={`Decrease quantity for ${item.itemCode}`}
                              onClick={() => {
                                const current = Number(editableData[item.itemBarcodeID]?.quantity) || 0;
                                const newValue =
                                  item.itemStatus === 'EXCHANGE' || item.itemStatus === 'RETURN' || item.itemStatus === 'exchange'
                                    ? Math.max(-Math.abs(current + 1), -1)
                                    : Math.max(current - 1, 1);
                                handleInputChange({ target: { value: newValue } }, item.itemBarcodeID, 'quantity');
                              }}
                            >
                              −
                            </button>

                            <input
                              type="number"
                              aria-label={`Quantity to add for ${item.itemCode}`}
                              value={editableData[item.itemBarcodeID]?.quantity ?? 0}
                              onChange={(e) => handleInputChange(e, item.itemBarcodeID, 'quantity')}
                              ref={(el) => (inputRefs.current[`${rowIndex}-0`] = el)}
                              onKeyDown={(e) => handleKeyDown(e, rowIndex, 0)}
                              className={styles.stepperInput}
                            />

                            <button
                              type="button"
                              className={styles.stepperBtn}
                              aria-label={`Increase quantity for ${item.itemCode}`}
                              onClick={() => {
                                const current = Number(editableData[item.itemBarcodeID]?.quantity) || 0;
                                const newValue =
                                  item.itemStatus === 'EXCHANGE' || item.itemStatus === 'RETURN' || item.itemStatus === 'exchange'
                                    ? Math.min(current - 1, -1)
                                    : current + 1;
                                handleInputChange({ target: { value: newValue } }, item.itemBarcodeID, 'quantity');
                              }}
                            >
                              +
                            </button>
                          </div>
                        </td>
                      )}

                      <td>
                        {grouped ? (
                          <span className={styles.groupId}>{item.group_id}</span>
                        ) : (
                          <span className={styles.muted}>—</span>
                        )}
                      </td>
                      <td className={styles.alignCenter}>
                        <button
                          type="button"
                          onClick={() => handlePlaceOrder(item.itemBarcodeID)}
                          className={styles.orderBtn}
                          disabled={isOrdering}
                        >
                          <ShoppingCart size={14} aria-hidden="true" />
                          {isOrdering ? 'Placing…' : 'Place order'}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          ) : (
            <div className={styles.noResults}>
              <Search size={22} aria-hidden="true" />
              <p className={styles.noResultsTitle}>No items match these filters</p>
              {hasActiveFilters && (
                <button type="button" className={styles.btnLink} onClick={clearFilters}>
                  Clear filters
                </button>
              )}
            </div>
          )}
        </div>

        {isEditing && (
          <div className={styles.editBar} role="region" aria-label="Unsaved stock changes">
            <div className={styles.editBarText}>
              <span>
                <strong>{editedCount}</strong> {editedCount === 1 ? 'item' : 'items'} changed
              </span>
              <span className={styles.editBarHint}>Arrow keys move between fields</span>
            </div>
            <div className={styles.editBarActions}>
              <button
                type="button"
                className={cx(styles.btn, styles.btnSecondary)}
                onClick={handleDiscard}
                disabled={isSubmitting}
              >
                <X size={15} aria-hidden="true" />
                Discard
              </button>
              <button
                type="button"
                className={cx(styles.btn, styles.btnPrimary)}
                onClick={handleUpdate}
                disabled={isSubmitting || editedCount === 0}
              >
                <Check size={15} aria-hidden="true" />
                {isSubmitting ? 'Submitting…' : 'Submit updates'}
              </button>
            </div>
          </div>
        )}

        {totalRows > 0 && (
          <div className={styles.footer}>
            <div className={styles.pageInfo}>
              <label className={styles.pageSizeLabel}>
                Rows per page
                <select
                  className={cx(styles.control, styles.pageSizeSelect)}
                  value={pageSize}
                  onChange={(e) => setPageSize(Number(e.target.value))}
                >
                  {PAGE_SIZE_OPTIONS.map((size) => (
                    <option key={size} value={size}>{size || 'All'}</option>
                  ))}
                </select>
              </label>
              <span>
                Showing {formatCount(pageStart + 1)}–{formatCount(pageEnd)} of {formatCount(totalRows)}
              </span>
            </div>

            {totalPages > 1 && (
              <nav className={styles.pager} aria-label="Pagination">
                <button type="button" className={styles.pageBtn} onClick={() => setPage(1)} disabled={currentPage === 1} aria-label="First page">
                  <ChevronsLeft size={16} aria-hidden="true" />
                </button>
                <button type="button" className={styles.pageBtn} onClick={() => setPage(currentPage - 1)} disabled={currentPage === 1} aria-label="Previous page">
                  <ChevronLeft size={16} aria-hidden="true" />
                </button>
                {getPageNumbers(currentPage, totalPages).map((pageNumber) => (
                  <button
                    key={pageNumber}
                    type="button"
                    className={cx(styles.pageBtn, pageNumber === currentPage && styles.pageBtnActive)}
                    onClick={() => setPage(pageNumber)}
                    aria-current={pageNumber === currentPage ? 'page' : undefined}
                  >
                    {pageNumber}
                  </button>
                ))}
                <button type="button" className={styles.pageBtn} onClick={() => setPage(currentPage + 1)} disabled={currentPage === totalPages} aria-label="Next page">
                  <ChevronRight size={16} aria-hidden="true" />
                </button>
                <button type="button" className={styles.pageBtn} onClick={() => setPage(totalPages)} disabled={currentPage === totalPages} aria-label="Last page">
                  <ChevronsRight size={16} aria-hidden="true" />
                </button>
              </nav>
            )}
          </div>
        )}
      </section>
    </div>
  );
};

export default View;
